import { injectable, inject } from 'inversify';
import { GTD_DOMAIN_SYMBOLS } from '../../../../infrastructure/di/GTDDomainSymbols';
import { ProjectRepository } from '../../infrastructure/repositories/ProjectRepository';
import { getMongoDb, Collection } from "@swoft/persistence";

/**
 * Query criteria for filtering projects
 */
export interface ProjectQueryCriteria {
  readonly status?: 'active' | 'someday-maybe' | 'completed' | 'cancelled' | 'all';
  readonly area?: string;
  readonly assignedTo?: string;
  readonly searchText?: string;
  readonly needsReview?: boolean;
  readonly createdBy?: string;
  readonly offset?: number;
  readonly limit?: number;
}

/**
 * Project view model for read operations
 */
export interface ProjectView {
  readonly id: string;
  readonly name: string;
  readonly desiredOutcome: string;
  readonly status: 'active' | 'someday-maybe' | 'completed' | 'cancelled';
  readonly createdBy: string;
  readonly createdAt: Date;
  readonly area?: string;
  readonly reviewDate?: Date;
  readonly completedAt?: Date;
  readonly nextActionCount: number;
  readonly completedActionCount: number;
  readonly needsAttention: boolean;
  readonly progress: {
    totalActions: number;
    completedActions: number;
    completionPercentage: number;
  };
}

/**
 * Query result with pagination
 */
export interface ProjectQueryResult {
  readonly projects: ProjectView[];
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
  readonly hasMore: boolean;
}

/**
 * ProjectReadService - Query Side (CQRS)
 * 
 * Handles all read operations for GTD projects.
 * Optimized for GTD "Review" phase - checking project status and progress.
 */
@injectable()
export class ProjectReadService {
  private collection: Collection;

  constructor(
    @inject(GTD_DOMAIN_SYMBOLS.ProjectRepository)
    _repository: ProjectRepository
  ) {
    const db = getMongoDb();
    this.collection = db.collection('gtd_projects');
  }

  /**
   * Get projects matching criteria
   * Core GTD principle: Projects need regular review to stay on track
   */
  async getProjects(criteria: ProjectQueryCriteria): Promise<ProjectQueryResult> {
    const {
      status = 'active',
      area,
      assignedTo,
      searchText,
      needsReview,
      createdBy,
      offset = 0,
      limit = 20
    } = criteria;

    // Build query
    const query: any = {};
    
    if (status !== 'all') {
      query.status = status;
    }
    
    if (area) {
      query.area = area;
    }
    
    if (assignedTo) {
      query.assignedTo = assignedTo;
    }
    
    if (createdBy) {
      query.createdBy = createdBy;
    }
    
    if (searchText) {
      query.$or = [
        { name: { $regex: searchText, $options: 'i' } },
        { desiredOutcome: { $regex: searchText, $options: 'i' } }
      ];
    }

    // Execute query with pagination
    const [projects, total] = await Promise.all([
      this.collection
        .find(query)
        .sort({ createdAt: -1 })
        .skip(offset)
        .limit(limit)
        .toArray(),
      this.collection.countDocuments(query)
    ]);

    // Map to view models with enhanced data
    const projectViews: ProjectView[] = await Promise.all(
      projects.map(async (doc) => {
        const actionStats = await this.getActionStatisticsForProject(doc._id.toString());
        
        return {
          id: doc._id.toString(),
          name: doc.name,
          desiredOutcome: doc.desiredOutcome,
          status: doc.status,
          createdBy: doc.createdBy,
          createdAt: doc.createdAt,
          area: doc.area,
          reviewDate: doc.reviewDate,
          completedAt: doc.completedAt,
          nextActionCount: actionStats.nextActionCount,
          completedActionCount: actionStats.completedActionCount,
          needsAttention: actionStats.needsAttention,
          progress: {
            totalActions: actionStats.totalActions,
            completedActions: actionStats.completedActions,
            completionPercentage: actionStats.completionPercentage
          }
        };
      })
    );

    // Filter by needsReview if specified
    const filteredProjects = needsReview 
      ? projectViews.filter(p => p.needsAttention)
      : projectViews;

    return {
      projects: filteredProjects,
      total,
      offset,
      limit,
      hasMore: total > offset + limit
    };
  }

  /**
   * Get a single project by ID
   */
  async getProjectById(projectId: string): Promise<ProjectView | null> {
    const doc = await this.collection.findOne({ _id: projectId as any });
    
    if (!doc) {
      return null;
    }

    const actionStats = await this.getActionStatisticsForProject(projectId);

    return {
      id: doc._id.toString(),
      name: doc.name,
      desiredOutcome: doc.desiredOutcome,
      status: doc.status,
      createdBy: doc.createdBy,
      createdAt: doc.createdAt,
      area: doc.area,
      reviewDate: doc.reviewDate,
      completedAt: doc.completedAt,
      nextActionCount: actionStats.nextActionCount,
      completedActionCount: actionStats.completedActionCount,
      needsAttention: actionStats.needsAttention,
      progress: {
        totalActions: actionStats.totalActions,
        completedActions: actionStats.completedActions,
        completionPercentage: actionStats.completionPercentage
      }
    };
  }

  /**
   * Get projects for GTD weekly review
   */
  async getProjectsForReview(personId: string): Promise<ProjectQueryResult> {
    const query = {
      createdBy: personId,
      status: { $in: ['active', 'someday-maybe'] }
    };

    const projects = await this.collection
      .find(query)
      .sort({ status: 1, createdAt: -1 })
      .toArray();

    const projectViews: ProjectView[] = await Promise.all(
      projects.map(async (doc) => {
        const actionStats = await this.getActionStatisticsForProject(doc._id.toString());
        
        return {
          id: doc._id.toString(),
          name: doc.name,
          desiredOutcome: doc.desiredOutcome,
          status: doc.status,
          createdBy: doc.createdBy,
          createdAt: doc.createdAt,
          area: doc.area,
          reviewDate: doc.reviewDate,
          completedAt: doc.completedAt,
          nextActionCount: actionStats.nextActionCount,
          completedActionCount: actionStats.completedActionCount,
          needsAttention: actionStats.needsAttention,
          progress: {
            totalActions: actionStats.totalActions,
            completedActions: actionStats.completedActions,
            completionPercentage: actionStats.completionPercentage
          }
        };
      })
    );

    return {
      projects: projectViews,
      total: projectViews.length,
      offset: 0,
      limit: projectViews.length,
      hasMore: false
    };
  }

  /**
   * Get projects by area for organizational purposes
   */
  async getProjectsByArea(area: string): Promise<ProjectQueryResult> {
    return this.getProjects({ area, status: 'active' });
  }

  /**
   * Get project statistics
   */
  async getProjectStatistics(): Promise<{
    total: number;
    active: number;
    somedayMaybe: number;
    completed: number;
    cancelled: number;
    needingReview: number;
    byArea: Record<string, number>;
  }> {
    const [
      total,
      active,
      somedayMaybe,
      completed,
      cancelled,
      byArea
    ] = await Promise.all([
      this.collection.countDocuments({}),
      this.collection.countDocuments({ status: 'active' }),
      this.collection.countDocuments({ status: 'someday-maybe' }),
      this.collection.countDocuments({ status: 'completed' }),
      this.collection.countDocuments({ status: 'cancelled' }),
      this.getCountByField('area')
    ]);

    // Get projects needing review (active projects with no next actions)
    const needingReview = await this.collection.countDocuments({
      status: 'active',
      nextActionIds: { $size: 0 }
    });

    return {
      total,
      active,
      somedayMaybe,
      completed,
      cancelled,
      needingReview,
      byArea
    };
  }

  /**
   * Get action statistics for a specific project
   */
  private async getActionStatisticsForProject(projectId: string): Promise<{
    nextActionCount: number;
    completedActionCount: number;
    totalActions: number;
    completedActions: number;
    completionPercentage: number;
    needsAttention: boolean;
  }> {
    const actionsCollection = getMongoDb().collection('gtd_next_actions');
    
    const [
      nextActionCount,
      completedActionCount
    ] = await Promise.all([
      actionsCollection.countDocuments({ 
        projectId,
        status: { $in: ['available', 'assigned'] }
      }),
      actionsCollection.countDocuments({ 
        projectId,
        status: 'completed'
      })
    ]);

    const totalActions = nextActionCount + completedActionCount;
    const completionPercentage = totalActions > 0 
      ? Math.round((completedActionCount / totalActions) * 100)
      : 0;

    // A project needs attention if it has no next actions at all
    const needsAttention = nextActionCount === 0 && completedActionCount === 0;

    return {
      nextActionCount,
      completedActionCount,
      totalActions,
      completedActions: completedActionCount,
      completionPercentage,
      needsAttention
    };
  }

  /**
   * Get counts grouped by a field
   */
  private async getCountByField(field: string): Promise<Record<string, number>> {
    const results = await this.collection.aggregate([
      {
        $group: {
          _id: `$${field}`,
          count: { $sum: 1 }
        }
      }
    ]).toArray();

    const counts: Record<string, number> = {};
    for (const result of results) {
      if (result._id) {
        counts[result._id] = result.count;
      }
    }
    return counts;
  }
}